import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  FOOD_HANDLER_STATES,
  type FoodHandlerCert,
  type FoodHandlerCertVerifyResult,
  type FoodHandlerCourseSession,
  type Page,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TAB_KEYS = ['certs', 'sessions', 'verify'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** Food handler certification pipeline — packages/modules/m25-public-health/src/routes/foodHandlerCerts.ts. */
export function FoodHandlerCerts() {
  const [tab, setTab] = useState<TabKey>('certs');
  return (
    <div>
      <PageHeader title="Food Handler Certification" subtitle="Course booking, training, medical screen and employer-verifiable certificate." />
      <Tabs
        tabs={[
          { key: 'certs', label: 'Certificates' },
          { key: 'sessions', label: 'Course sessions' },
          { key: 'verify', label: 'Verify a certificate' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'certs' ? <CertsTab /> : null}
      {tab === 'sessions' ? <SessionsTab /> : null}
      {tab === 'verify' ? <VerifyTab /> : null}
    </div>
  );
}

function CertsTab() {
  const { persona } = useAuth();
  const isPhaStaff = (persona?.roles.includes('PH_INSPECTOR') || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showApply, setShowApply] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<FoodHandlerCert>>(`/api/v1/m25/food-handler-certs?${qs.toString()}`);

  return (
    <div>
      {!isPhaStaff ? (
        <div className="mb-4">
          <ErrorNote message="Listing certificates requires PH_INSPECTOR or SENIOR_PHI." />
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <input className="field-input !w-48" placeholder="Search NIN / ref no…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-52" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            {FOOD_HANDLER_STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowApply(true)}>
          + Apply on behalf
        </button>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No certificates match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref no.</th>
                <th className="table-head">NIN</th>
                <th className="table-head">State</th>
                <th className="table-head">Cert serial</th>
                <th className="table-head">Expires</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(c.id)}>
                  <td className="table-cell font-mono text-xs">{c.refNo}</td>
                  <td className="table-cell font-medium">{c.nin}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.state} />
                  </td>
                  <td className="table-cell font-mono text-xs">{c.certSerial ?? '—'}</td>
                  <td className="table-cell text-xs">{formatDate(c.expiresAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showApply ? (
        <ApplyModal
          onClose={() => setShowApply(false)}
          onDone={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? (
        <CertDetailModal id={detailId} isPhaStaff={isPhaStaff} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function ApplyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const sessionsQ = useApiQuery<{ items: FoodHandlerCourseSession[] }>('/api/v1/m25/food-handler-course-sessions');
  const [nin, setNin] = useState('');
  const [courseSessionId, setCourseSessionId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m25/food-handler-certs', { nin, courseSessionId: courseSessionId || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to apply');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Apply for a food handler certificate" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Applicant NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. SC870512033" />
        </Field>
        <Field label="Course session (optional)">
          <select className="field-input" value={courseSessionId} onChange={(e) => setCourseSessionId(e.target.value)}>
            <option value="">Not booked yet</option>
            {(sessionsQ.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.venue} — {formatDateTime(s.startsAt)} ({s.spotsRemaining ?? s.capacity - s.bookedCount} left)
              </option>
            ))}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || nin.trim().length < 5}>
            {busy ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CertDetailModal({ id, isPhaStaff, onClose, onChanged }: { id: string; isPhaStaff: boolean; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<FoodHandlerCert>(`/api/v1/m25/food-handler-certs/${id}`);
  const sessionsQ = useApiQuery<{ items: FoodHandlerCourseSession[] }>(
    detailQ.data?.state === 'APPLIED' ? '/api/v1/m25/food-handler-course-sessions' : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [courseSessionId, setCourseSessionId] = useState('');
  const [screenNotes, setScreenNotes] = useState('');
  const [reason, setReason] = useState('');

  const cert = detailQ.data;

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/food-handler-certs/${id}${path}`, body);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Food handler certificate" onClose={onClose}>
      {detailQ.loading || !cert ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs">{cert.refNo}</span>
            <StatusBadge status={cert.state} />
          </div>
          <dl className="space-y-1">
            <div className="flex justify-between">
              <dt className="text-slate-500">NIN</dt>
              <dd className="font-medium">{cert.nin}</dd>
            </div>
            {cert.feeAmountScr != null ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Fee</dt>
                <dd className="font-medium">{scr(cert.feeAmountScr)}</dd>
              </div>
            ) : null}
            {cert.certSerial ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Cert serial</dt>
                <dd className="font-mono text-xs">{cert.certSerial}</dd>
              </div>
            ) : null}
          </dl>
          {cert.rejectionReason ? <p className="text-xs text-rose-600">{cert.rejectionReason}</p> : null}

          {cert.state === 'APPLIED' ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <Field label="Book a course session">
                <select className="field-input" value={courseSessionId} onChange={(e) => setCourseSessionId(e.target.value)}>
                  <option value="">Select a session…</option>
                  {(sessionsQ.data?.items ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.venue} — {formatDateTime(s.startsAt)}
                    </option>
                  ))}
                </select>
              </Field>
              <button className="btn-primary" onClick={() => run('/book-course', { courseSessionId })} disabled={busy || !courseSessionId}>
                Book course
              </button>
            </div>
          ) : null}

          {cert.state === 'COURSE_BOOKED' && isPhaStaff ? (
            <div className="border-t border-slate-100 pt-3">
              <button className="btn-primary" onClick={() => run('/training-complete')} disabled={busy}>
                Mark training complete
              </button>
            </div>
          ) : null}

          {cert.state === 'TRAINING_COMPLETED' && isPhaStaff ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <Field label="Medical screen notes (optional)">
                <textarea className="field-input" rows={2} value={screenNotes} onChange={(e) => setScreenNotes(e.target.value)} />
              </Field>
              <div className="flex gap-2">
                <button className="btn-success" onClick={() => run('/medical-screen', { passed: true, notes: screenNotes || undefined })} disabled={busy}>
                  Passed
                </button>
                <button className="btn-danger" onClick={() => run('/medical-screen', { passed: false, notes: screenNotes || undefined })} disabled={busy}>
                  Failed
                </button>
              </div>
            </div>
          ) : null}

          {cert.state === 'FEE_PENDING' ? <p className="text-xs text-slate-400">Awaiting SeyPay payment webhook to issue.</p> : null}

          {(['APPLIED', 'COURSE_BOOKED', 'TRAINING_COMPLETED'] as const).includes(cert.state as never) && isPhaStaff ? (
            <div className="space-y-1.5 border-t border-slate-100 pt-3">
              <div className="flex gap-1.5">
                <input className="field-input !py-1 text-xs" placeholder="Rejection reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
                <button className="btn-danger shrink-0 !px-2.5 !py-1 text-xs" disabled={reason.trim().length < 3 || busy} onClick={() => run('/reject', { reason })}>
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {cert.state === 'ISSUED' && isPhaStaff ? (
            <div className="flex gap-1.5 border-t border-slate-100 pt-3">
              <input className="field-input !py-1 text-xs" placeholder="Revoke reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button className="btn-danger shrink-0 !px-2.5 !py-1 text-xs" disabled={reason.trim().length < 3 || busy} onClick={() => run('/revoke', { reason })}>
                Revoke
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function SessionsTab() {
  const { persona } = useAuth();
  const isPhaStaff = (persona?.roles.includes('PH_INSPECTOR') || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const [district, setDistrict] = useState('');
  const [showAll, setShowAll] = useState(false);
  const qs = new URLSearchParams();
  if (district) qs.set('district', district);
  if (showAll) qs.set('all', 'true');
  const listQ = useApiQuery<{ items: FoodHandlerCourseSession[] }>(`/api/v1/m25/food-handler-course-sessions?${qs.toString()}`);

  const [venue, setVenue] = useState('');
  const [newDistrict, setNewDistrict] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [capacity, setCapacity] = useState('30');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m25/food-handler-course-sessions', {
        venue,
        district: newDistrict,
        startsAt: new Date(startsAt).toISOString(),
        capacity: Number.parseInt(capacity, 10),
      });
      setVenue('');
      listQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create session');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <div className="mb-3 flex flex-wrap gap-2">
          <input className="field-input !w-44" placeholder="Filter district…" value={district} onChange={(e) => setDistrict(e.target.value)} />
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Include past sessions
          </label>
        </div>
        {listQ.error ? <ErrorNote message={listQ.error} /> : null}
        <div className="card overflow-x-auto">
          {listQ.loading ? (
            <Spinner />
          ) : (listQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No course sessions" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Venue</th>
                  <th className="table-head">District</th>
                  <th className="table-head">Starts</th>
                  <th className="table-head text-right">Spots</th>
                </tr>
              </thead>
              <tbody>
                {listQ.data!.items.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{s.venue}</td>
                    <td className="table-cell">{s.district}</td>
                    <td className="table-cell text-xs">{formatDateTime(s.startsAt)}</td>
                    <td className="table-cell text-right tabular-nums">
                      {s.spotsRemaining ?? s.capacity - s.bookedCount}/{s.capacity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">New course session</h2>
        {!isPhaStaff ? <ErrorNote message="Requires PH_INSPECTOR or SENIOR_PHI." /> : null}
        {error ? (
          <div className="my-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <div className="space-y-2.5">
          <Field label="Venue">
            <input className="field-input" value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="e.g. Victoria Community Hall" />
          </Field>
          <Field label="District">
            <input className="field-input" value={newDistrict} onChange={(e) => setNewDistrict(e.target.value)} placeholder="e.g. Victoria" />
          </Field>
          <Field label="Starts at">
            <input type="datetime-local" className="field-input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label="Capacity">
            <input type="number" min="1" max="500" className="field-input" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
          <button className="btn-primary w-full" onClick={submit} disabled={busy || !isPhaStaff || !venue.trim() || !newDistrict.trim() || !startsAt}>
            {busy ? 'Creating…' : 'Create session'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VerifyTab() {
  const [certSerial, setCertSerial] = useState('');
  const [result, setResult] = useState<FoodHandlerCertVerifyResult | null>(null);
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await api.get<FoodHandlerCertVerifyResult>(`/api/v1/m25/food-handler-certs/verify/${encodeURIComponent(certSerial.trim())}`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card max-w-md p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Employer-verifiable check</h2>
      <p className="mb-3 text-xs text-slate-500">Any signed-in account may check a certificate by its serial — no diagnosis or personal detail is exposed.</p>
      <div className="flex gap-2">
        <input className="field-input" value={certSerial} onChange={(e) => setCertSerial(e.target.value)} placeholder="Certificate serial…" />
        <button className="btn-secondary shrink-0" onClick={verify} disabled={busy || !certSerial.trim()}>
          Verify
        </button>
      </div>
      {result ? (
        <div className={`mt-4 rounded-lg p-3 text-sm ${result.found && result.valid ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>
          {!result.found ? (
            'Not found.'
          ) : (
            <>
              <div className="font-semibold">{result.valid ? 'Valid' : 'Not currently valid'}</div>
              <div className="mt-1 text-xs">
                State: {result.state} · Issued {formatDate(result.issuedAt)} · Expires {formatDate(result.expiresAt)}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
